import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, AuthContextType, UserLevel } from '@/types/auth.types';
import { authService } from '@/services/authService';
import { tokenManager } from '@/services/api';

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Props for AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [utilizator, setUtilizator] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state on app start
  useEffect(() => {
    initializeAuth();
  }, []);

  // Initialize authentication state
  const initializeAuth = async () => {
    try {
      setLoading(true);
      
      // Check if user has valid token
      if (authService.isAuthenticated()) {
        // First try to get stored user data for immediate availability
        const storedUser = authService.getCurrentUser();
        if (storedUser) {
          setUtilizator(storedUser);
          
          // After setting stored user, navigate to appropriate dashboard
          const userLevel = storedUser.user_level_id || storedUser.user_level?.id || 0;
          let defaultPage = 'dashboard';

          switch (userLevel) {
            case 1:
              defaultPage = 'maternity-dashboard';
              break;
            case 2:
              // For hospital users, prefer the stored section, falling back to
              // the account's assigned section rather than the generic (both
              // sections mixed together) hospital-dashboard.
              const storedSection = sessionStorage.getItem('activeSection');
              const section = storedSection === 'prematuri' || storedSection === 'chd'
                ? storedSection
                : (storedUser.assigned_section === 'prematuri' ? 'prematuri' : 'chd');
              defaultPage = section === 'prematuri' ? 'prematuri-dashboard' : 'chd-dashboard';
              break;
            case 3:
              defaultPage = 'admin-dashboard';
              break;
          }

          // Trigger navigation after auth initialization
          setTimeout(() => {
            const navigationEvent = new CustomEvent('navigate', { detail: { page: defaultPage } });
            window.dispatchEvent(navigationEvent);
          }, 100);
        }
        
        // Try to refresh user profile from server
        try {
          const user = await authService.getProfile();
          setUtilizator(user);
          authService.setCurrentUser(user);
        } catch (profileError) {
          // If profile fetch fails but we have stored user, keep using it
          console.warn('Could not refresh user profile:', profileError);
          if (!storedUser) {
            throw profileError;
          }
        }
      } else {
        // Clear any stale data
        authService.clearCurrentUser();
        tokenManager.clearTokens();
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      // Clear tokens if profile fetch fails
      authService.clearCurrentUser();
      tokenManager.clearTokens();
      setUtilizator(null);
    } finally {
      setLoading(false);
    }
  };

  // Login function
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await authService.login(credentials);
      const user = response.user;
      
      setUtilizator(user);
      authService.setCurrentUser(user);
      
      // Force re-render by updating loading state
      setLoading(false);
      
      // Small delay to ensure state updates have propagated
      setTimeout(() => {
        // Trigger navigation event after successful login based on user level
        const getDefaultPage = (userLevel: any): string => {
          // Handle user_level as an object or number
          let level: number;

          if (typeof userLevel === 'object' && userLevel !== null) {
            // If it's an object, use the id or level_order
            level = userLevel.id || userLevel.level_order || 0;
          } else if (typeof userLevel === 'string') {
            level = parseInt(userLevel, 10);
          } else {
            level = userLevel;
          }

          // Hospital users are always routed to their assigned registry
          // section's dashboard, never the generic (unfiltered, both
          // sections mixed together) hospital-dashboard - matches the
          // section AppContentWrapper resolves the same user to.
          const hospitalSectionPage = () =>
            user.assigned_section === 'prematuri' ? 'prematuri-dashboard' : 'chd-dashboard';

          switch (level) {
            case 1:
            case UserLevel.MATERNITY:
              return 'maternity-dashboard';
            case 2:
            case UserLevel.HOSPITAL:
              return hospitalSectionPage();
            case 3:
            case UserLevel.ADMINISTRATOR:
              return 'admin-dashboard';
            default:
              // Default based on level value
              if (level >= 3) return 'admin-dashboard';
              if (level === 2) return hospitalSectionPage();
              if (level === 1) return 'maternity-dashboard';
              return 'dashboard';
          }
        };

        const defaultPage = getDefaultPage(user.user_level);
        const navigationEvent = new CustomEvent('navigate', { detail: { page: defaultPage } });
        window.dispatchEvent(navigationEvent);
      }, 100);
    } catch (error) {
      // Clear any tokens on login failure
      tokenManager.clearTokens();
      authService.clearCurrentUser();
      throw error;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state
      setUtilizator(null);
      authService.clearCurrentUser();
      tokenManager.clearTokens();
      // Clear section selection for hospital users (state + storage) so a
      // different user logging in afterwards doesn't inherit a stale section
      window.dispatchEvent(new CustomEvent('clearSection'));
    }
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    try {
      await authService.refreshToken();
      // Token is automatically stored by the service
    } catch (error) {
      // If refresh fails, log out the user
      await logout();
      throw error;
    }
  };

  // Check if user has a specific permission
  const arePermisiune = (permisiune: string): boolean => {
    if (!utilizator) return false;
    
    // Get numeric level from user_level object
    const userLevel = utilizator.user_level_id || utilizator.user_level?.id || 0;

    // Basic permission logic based on user level
    switch (permisiune) {
      case 'view_patients':
        return userLevel >= UserLevel.MATERNITY;
      
      case 'create_patients':
        return userLevel >= UserLevel.MATERNITY;
      
      case 'edit_patients':
        return userLevel >= UserLevel.HOSPITAL;
      
      case 'delete_patients':
        return userLevel >= UserLevel.ADMINISTRATOR;
      
      case 'view_diagnoses':
        return userLevel >= UserLevel.MATERNITY;
      
      case 'create_diagnoses':
        return userLevel >= UserLevel.HOSPITAL;
      
      case 'edit_diagnoses':
        return userLevel >= UserLevel.HOSPITAL;
      
      case 'delete_diagnoses':
        return userLevel >= UserLevel.ADMINISTRATOR;
      
      case 'view_health_markers':
        return userLevel >= UserLevel.HOSPITAL;
      
      case 'create_health_markers':
        return userLevel >= UserLevel.HOSPITAL;
      
      case 'view_maternal_data':
        return userLevel >= UserLevel.MATERNITY;
      
      case 'create_maternal_data':
        return userLevel >= UserLevel.MATERNITY;
      
      case 'edit_maternal_data':
        return userLevel >= UserLevel.HOSPITAL;
      
      case 'view_users':
        return userLevel >= UserLevel.ADMINISTRATOR;
      
      case 'create_users':
        return userLevel >= UserLevel.ADMINISTRATOR;
      
      case 'edit_users':
        return userLevel >= UserLevel.ADMINISTRATOR;
      
      case 'delete_users':
        return userLevel >= UserLevel.ADMINISTRATOR;
      
      case 'view_reports':
        return userLevel >= UserLevel.HOSPITAL;
      
      case 'export_data':
        return userLevel >= UserLevel.ADMINISTRATOR;
      
      default:
        return false;
    }
  };

  // Get user level (numeric)
  const nivelUtilizator = utilizator?.user_level_id || utilizator?.user_level?.id || null;

  // Check if user is authenticated - use direct token check for immediate updates
  const hasValidToken = authService.isAuthenticated();
  const isAuthenticated = !!utilizator && hasValidToken;

  // Set up token refresh timer
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiry = () => {
      if (tokenManager.isTokenExpired()) {
        refreshToken().catch((error) => {
          console.error('Auto token refresh failed:', error);
          logout();
        });
      }
    };

    // Check token expiry every 5 minutes
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Context value
  const contextValue: AuthContextType = {
    utilizator,
    loading,
    login,
    logout,
    refreshToken,
    arePermisiune,
    nivelUtilizator,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;